import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { toast, Toaster } from 'react-hot-toast';

import { SuperAdminService } from '@/services/superadmin.service';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import {
  Building, X, Mail, MessageSquare, Send, Users, Calendar, ChevronRight, CheckCircle2, Ban, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { NotificationBell } from '@/components/NotificationBell';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface TenantCard {
  id: string;
  name: string;
  subdomain: string;
  logo_url: string | null;
  theme_color: string | null;
  user_count: number;
  event_count: number;
  created_at: string;
  createdAt?: string;
  status?: string;
  is_pending_invite?: boolean;
  invite_email?: string;
}

const SuperAdminDashboard: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const [tenants, setTenants] = useState<TenantCard[]>([]);
  const [loading, setLoading] = useState(true);

  // Confirmation modal for delete and revoke
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    actionType: 'delete' | 'revoke';
    targetName: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    actionType: 'delete',
    targetName: '',
    onConfirm: async () => {},
  });

  // Invite modal states
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [inviteErrorMsg, setInviteErrorMsg] = useState('');

  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchTenants();
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('invite') === 'true') {
      setInviteOpen(true);
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const data = await SuperAdminService.getClients();
      // Ensure only true tenant / institution records are shown (exclude any event participants)
      const sanitized = (data || []).filter((t: any) => {
        if (!t.is_pending_invite) return true;
        const role = (t.role || '').toLowerCase().trim();
        const isEventRole = ['student', 'participant', 'mentor', 'faculty', 'judge', 'evaluator', 'volunteer'].includes(role);
        return !t.event_id && !isEventRole;
      });
      setTenants(sanitized);
    } catch (err) {
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inviteName.length < 3) {
      toast.error('Institution name must be at least 3 characters');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setInviteLoading(true);
    try {
      await SuperAdminService.inviteClient(inviteName, inviteEmail, inviteMessage.trim() || null);
      setInviteResult('success');
      await fetchTenants();
    } catch (err: any) {
      setInviteErrorMsg(err.response?.data?.error?.message || 'Failed to send invitation');
      setInviteResult('error');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCloseInviteModal = () => {
    setInviteOpen(false);
    setInviteResult('idle');
    setInviteName('');
    setInviteEmail('');
    setInviteMessage('');
  };

  const handleTryInviteAgain = () => {
    setInviteResult('idle');
  };



  return (
    <div className="min-h-screen bg-[#F7F2EA] text-[#243238] flex w-full relative">
      <Toaster position="top-right" />
      <SuperAdminSidebar onInviteClick={() => setInviteOpen(true)} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader onInviteClick={() => setInviteOpen(true)} title="Super Admin Console" />

        {/* Top Header - Desktop only */}
        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <div>
            <h1 className="text-sm sm:text-base font-extrabold tracking-tight leading-tight text-[#243238] uppercase">
              Super Admin Console
            </h1>
          </div>
          <div className="flex items-center gap-4 select-none">
            <NotificationBell type="superadmin" />
            <img
              src={logoImg}
              alt="Sthitih Logo"
              className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5"
            />
          </div>
        </header>

        {/* Body Content */}
        <main className="flex-grow p-4 sm:p-8 max-w-[2500px]">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#4A5558] mb-6">Your Clients</h2>
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#1F5A43]/20 border-t-[#1F5A43] rounded-full animate-spin"></div>
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#DDD0BE] rounded-[18px] max-w-sm mx-auto bg-[#FFFDF9] shadow-earth">
              <Building className="w-10 h-10 text-[#8D8A83] mx-auto mb-4" />
              <p className="text-[#6F706C] text-sm mb-4">No tenants found</p>
              <button
                onClick={() => setInviteOpen(true)}
                className="text-[#1F5A43] font-bold hover:underline text-sm cursor-pointer"
              >
                Invite your first client
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tenants.map((tenant, idx) => (
                <motion.div
                  key={tenant.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                  onClick={() => navigate(`/events/tenants/${tenant.id}`)}
                  className="rounded-[18px] overflow-hidden cursor-pointer border border-[#E4D8C8] hover:border-[#1F5A43] shadow-earth hover:shadow-earth-lg transition-all duration-350 bg-[#FFFDF9] flex flex-col justify-between"
                >
                  <div>
                    {/* Theme Color Indicator Strip */}
                    <div
                      className="h-2 w-full"
                      style={{
                        backgroundColor:
                          tenant.status === 'revoked'
                            ? '#D64545'
                            : tenant.status === 'pending'
                            ? '#D97706'
                            : tenant.theme_color || '#1F5A43'
                      }}
                    />

                    <div className="p-6 pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-xs"
                            style={{
                              backgroundColor:
                                tenant.status === 'revoked'
                                  ? '#D64545'
                                  : tenant.status === 'pending'
                                  ? '#D97706'
                                  : tenant.theme_color || '#1F5A43'
                            }}
                          >
                            {tenant.name.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-[#243238] text-sm line-clamp-1">{tenant.name}</h3>
                            </div>
                            <p className="text-[10px] text-[#6F706C] font-medium mt-0.5">{tenant.invite_email || (tenant as any).contact_email || 'Organization Client'}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                          tenant.status === 'revoked'
                            ? 'bg-rose-100 text-rose-800 border border-rose-200'
                            : tenant.status === 'pending'
                            ? 'bg-amber-100 text-amber-900 border border-amber-200'
                            : 'bg-[#1F5A43]/10 text-[#1F5A43] border border-[#1F5A43]/20'
                        }`}>
                          {tenant.status === 'revoked' ? 'Revoked' : tenant.status === 'pending' ? 'Pending' : tenant.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-[#6F706C]">
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-[#1F5A43]" />
                          <span>{tenant.user_count} Admins & Staff</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-[#8A4B22]" />
                          <span>{tenant.event_count} Events hosted</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer & Action Buttons */}
                  <div className="px-6 pb-4">
                    {(tenant.status === 'pending' || tenant.status === 'revoked') && (
                      <div className="pt-3 mb-2 border-t border-[#E4D8C8]/60 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        {tenant.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmModal({
                                isOpen: true,
                                actionType: 'revoke',
                                targetName: tenant.name,
                                onConfirm: async () => {
                                  try {
                                    await SuperAdminService.revokeInvitation(tenant.id);
                                    toast.success(`Invitation for ${tenant.name} revoked`);
                                    fetchTenants();
                                  } catch {
                                    toast.error('Failed to revoke invitation');
                                  }
                                }
                              });
                            }}
                            className="px-2.5 py-1 rounded-lg text-[10px] font-bold text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <Ban className="w-3 h-3" />
                            <span>Revoke</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmModal({
                              isOpen: true,
                              actionType: 'delete',
                              targetName: tenant.name,
                              onConfirm: async () => {
                                try {
                                  await SuperAdminService.deleteInvitation(tenant.id);
                                  toast.success(`Invitation for ${tenant.name} deleted permanently`);
                                  fetchTenants();
                                } catch {
                                  toast.error('Failed to delete invitation');
                                }
                              }
                            });
                          }}
                          className="px-2.5 py-1 rounded-lg text-[10px] font-bold text-rose-800 bg-rose-50 hover:bg-rose-100 border border-rose-200 flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}

                    <div className="pt-2 border-t border-[#E4D8C8]/40 flex items-center justify-between text-[10px] text-[#8D8A83]">
                      <span>Invited / Joined {new Date(tenant.createdAt || tenant.created_at || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <ChevronRight className="w-4 h-4 text-[#8D8A83]" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Invite Modal */}
      <AnimatePresence>
        {inviteOpen && (
          <div 
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={handleCloseInviteModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md p-8 rounded-[18px] border border-[#E4D8C8] bg-[#FFFDF9] shadow-earth"
            >
              {inviteResult === 'idle' ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-[#243238]">Invite New Client</h3>
                    <button onClick={handleCloseInviteModal} className="p-1 rounded-lg hover:bg-[#EFE5D6] text-[#8D8A83]">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleInviteSubmit} className="space-y-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#4A5558] uppercase mb-2">Institution Name</label>
                      <div className="relative">
                        <Building className="w-4 h-4 text-[#8D8A83] absolute left-3 top-3.5" />
                        <input
                          type="text"
                          value={inviteName}
                          onChange={(e) => setInviteName(e.target.value)}
                          placeholder="e.g. Stanford University"
                          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-[#DCCFBE] focus:outline-none focus:border-[#1F5A43] focus:ring-2 focus:ring-[#1F5A43] transition-all bg-white text-[#243238]"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#4A5558] uppercase mb-2">Admin Email</label>
                      <div className="relative">
                        <Mail className="w-4 h-4 text-[#8D8A83] absolute left-3 top-3.5" />
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="admin@university.edu"
                          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-[#DCCFBE] focus:outline-none focus:border-[#1F5A43] focus:ring-2 focus:ring-[#1F5A43] transition-all bg-white text-[#243238]"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#4A5558] uppercase mb-2">Custom Welcome Message (Optional)</label>
                      <div className="relative">
                        <MessageSquare className="w-4 h-4 text-[#8D8A83] absolute left-3 top-3.5" />
                        <textarea
                          value={inviteMessage}
                          onChange={(e) => setInviteMessage(e.target.value)}
                          placeholder="Welcome to Sthitih Event Platform..."
                          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-[#DCCFBE] focus:outline-none focus:border-[#1F5A43] focus:ring-2 focus:ring-[#1F5A43] transition-all h-24 resize-none bg-white text-[#243238]"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={inviteLoading}
                      className="w-full btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {inviteLoading ? 'Sending...' : 'Send Invitation'}
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                </>
              ) : inviteResult === 'success' ? (
                <div className="text-center py-4 space-y-5">
                  <div className="w-16 h-16 rounded-full bg-[#DFF5E7] text-[#1F7A4F] flex items-center justify-center mx-auto shadow-xs border border-[#1F7A4F]/20">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-[#243238]">Invitation Sent!</h3>
                    <p className="text-xs text-[#6F706C] max-w-xs mx-auto">
                      An onboarding email has been sent to <strong className="text-[#243238]">{inviteEmail}</strong> for <strong className="text-[#243238]">{inviteName}</strong>.
                    </p>
                  </div>
                  <button
                    onClick={handleCloseInviteModal}
                    className="w-full btn-primary"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="text-center py-4 space-y-5">
                  <div className="w-16 h-16 rounded-full bg-[#FCE8E8] text-[#D64545] flex items-center justify-center mx-auto shadow-xs border border-[#D64545]/20">
                    <X className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-[#D64545]">Invitation Failed</h3>
                    <p className="text-xs text-[#6F706C] max-w-xs mx-auto">
                      {inviteErrorMsg || 'Something went wrong while trying to send the invitation.'}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleTryInviteAgain}
                      className="flex-1 btn-secondary"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleCloseInviteModal}
                      className="flex-1 py-3 bg-[#EFE5D6] hover:bg-[#DDD0BE] text-[#5A4634] font-semibold rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal for Delete and Revoke Actions */}
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        actionType={confirmModal.actionType}
        targetName={confirmModal.targetName}
      />
    </div>
  );
};

export default SuperAdminDashboard;
